const productCardStates = new WeakMap()
const initializedCards = new WeakSet()
const initializedRoots = new WeakSet()

let deferredStateObserver = null

const formatMoney = (valueInCents) => {
  const moneyFormat = window.Shopify?.money_format || window.__NOVA_GLOBAL__?.moneyFormat

  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(
      valueInCents,
      moneyFormat,
    )
  }

  const amount = Number(valueInCents || 0)

  if (window.__NOVA_GLOBAL__?.moneyFormat) {
    const formattedAmount = (amount / 100).toFixed(2)
    return window.__NOVA_GLOBAL__.moneyFormat.replace('{{amount}}', formattedAmount)
  }

  return (amount / 100).toFixed(2)
}

const formatCardPrice = (card, valueInCents) => {
  const formattedPrice = formatMoney(valueInCents)
  const template = card.dataset.fromPriceTemplate || ''

  if (!template) return formattedPrice

  return template.replace('__PRICE__', formattedPrice)
}

const buildVariantUrl = (baseUrl, variantId) =>
  variantId ? `${baseUrl}?variant=${variantId}` : baseUrl

const buildProductUrl = (itemUrl, fallbackUrl, variantId) => {
  const rawUrl = itemUrl || fallbackUrl || ''
  if (!rawUrl) return ''

  try {
    const url = new URL(rawUrl, window.location.origin)

    if (variantId) {
      url.searchParams.set('variant', variantId)
    } else {
      url.searchParams.delete('variant')
    }

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return buildVariantUrl(rawUrl, variantId)
  }
}

const buildVariantTitle = (baseTitle, variant) => {
  let variantBaseTitle = variant?.name?.split('-')[0]?.trim()
  if (variantBaseTitle && variant?.option1) {
    return `${variantBaseTitle} - ${variant.option1}`
  }

  variantBaseTitle = baseTitle.split('-')[0]?.trim()
  if (variantBaseTitle && variant?.option1) {
    return `${variantBaseTitle} - ${variant.option1}`
  }

  return baseTitle
}

const buildSizedImage = (image) => {
  if (!image) return ''

  const isSmall = image
    .split('_')
    .some((fragment) => fragment.includes('x480') || fragment.includes('480x'))

  let sizedImage = image
  if (!isSmall) {
    const fragments = image.split('.')
    fragments[fragments.length - 2] = `${fragments[fragments.length - 2]}_480x`
    sizedImage = fragments.join('.')
  }

  return `${sizedImage}&width=480&height=640`
}

const getCardState = (card) => productCardStates.get(card) || null

const setCardState = (card, state) => {
  productCardStates.set(card, state)
}

const handleCardClick = (event) => {
  const swatchButton = event.target.closest?.('.js-product-card-v2-swatch')
  if (swatchButton) {
    event.preventDefault()
    const card = swatchButton.closest('.js-product-card-v2')
    if (!card) return

    updateProductCard(card, Number(swatchButton.dataset.index))
    return
  }

  const productLink = event.target.closest?.('.js-product-card-v2-link')
  if (!productLink) return
}

const normalizeStateItems = (state) => {
  if (Array.isArray(state?.items) && state.items.length) {
    return state.items
  }

  if (!state?.swatches?.length) {
    return []
  }

  return state.swatches.map((swatch, index) => ({
    name: swatch,
    style: state.colorValues?.[index] || 'transparent',
    variant: state.variants?.[index] || null,
    images: state.images?.[index] || [],
    url: state.urls?.[index] || '',
  }))
}

const renderSwatchButtons = (card, state) => {
  const swatchContainer = card.querySelector('.pcard-v2__swatch-container')
  const swatchWrapper = card.querySelector('.js-product-card-v2-swatches')
  const swatchMore = card.querySelector('.js-product-card-v2-swatch-more')
  const skeleton = card.querySelector('.js-product-card-v2-swatches-skeleton')
  const items = normalizeStateItems(state)

  if (!swatchWrapper) return

  if (!items.length) {
    swatchWrapper.innerHTML = ''
    swatchWrapper.classList.add('hidden')
    if (swatchMore) {
      swatchMore.innerHTML = ''
      swatchMore.classList.add('hidden')
    }
    if (skeleton) skeleton.classList.add('hidden')
    if (swatchContainer) swatchContainer.classList.add('hidden')
    return
  }

  if (swatchContainer) swatchContainer.classList.remove('hidden')

  const swatchesMarkup = items.map((item, index) => {
    const variant = item.variant
    if (!variant) return ''

    const swatchStyle = item.style || 'transparent'
    const soldOutClass = variant.available ? '' : ' pcard-v2__swatch--sold-out'
    const activeClass = index === 0 ? ' pcard-v2__swatch--active' : ''

    return `
      <button
        class="pcard-v2__swatch js-product-card-v2-swatch${activeClass}${soldOutClass}"
        data-index="${index}"
        data-variant-id="${variant.id}"
        aria-label="${item.name}">
        <span style="background: ${swatchStyle};">${item.name}</span>
      </button>
    `
  }).join('')

  swatchWrapper.innerHTML = swatchesMarkup
  swatchWrapper.classList.remove('hidden')
  if (skeleton) skeleton.classList.add('hidden')

  if (swatchMore) {
    const totalSwatchCount = Number(state.swatchCount || items.length)
    if (totalSwatchCount > 3) {
      swatchMore.innerHTML = `<a href="${card.dataset.baseUrl}">+${totalSwatchCount - 3}</a>`
      swatchMore.classList.remove('hidden')
    } else {
      swatchMore.innerHTML = ''
      swatchMore.classList.add('hidden')
    }
  }
}

const updateProductCard = (card, swatchIndex) => {
  const state = getCardState(card)
  const item = normalizeStateItems(state)?.[swatchIndex]
  const variant = item?.variant
  if (!state || !variant) return

  const baseTitle = card.dataset.baseTitle || card.getAttribute('title') || ''
  const baseUrl = card.dataset.baseUrl || ''
  const unit = card.dataset.unit || ''
  const title = buildVariantTitle(baseTitle, variant)
  const url = buildProductUrl(item?.url, baseUrl, variant.id)
  const primaryImage = buildSizedImage(
    variant.featured_image?.src || item?.images?.[0] || '',
  )
  let secondaryImage = buildSizedImage(item?.images?.[1] || '')
  if (variant.featured_image?.src && item?.images?.[0]) {
    secondaryImage = buildSizedImage(item.images[0])
  }

  card.setAttribute('title', title)
  card.setAttribute('data-variant-id', variant.id)
  card.setAttribute('sku', variant.sku || '')
  card.setAttribute('data-analytics-price', Number(variant.price || 0) / 100)

  card.querySelectorAll('.js-product-card-v2-link').forEach((link) => {
    link.setAttribute('href', url)
  })

  const titleElement = card.querySelector('.js-product-card-v2-title')
  if (titleElement) {
    titleElement.textContent = title
  }

  const primaryImageElement = card.querySelector('.js-product-card-v2-primary-image')
  if (primaryImageElement && primaryImage) {
    primaryImageElement.src = primaryImage
  }

  const secondaryImageWrapper = card.querySelector('.pcard-v2__image--secondary')
  const secondaryImageElement = card.querySelector('.js-product-card-v2-secondary-image')
  if (secondaryImageWrapper && secondaryImageElement) {
    if (secondaryImage) {
      secondaryImageElement.src = secondaryImage
      secondaryImageWrapper.classList.remove('hidden')
      card.classList.add('pcard-v2--enable-hover')
    } else {
      secondaryImageElement.removeAttribute('src')
      secondaryImageWrapper.classList.add('hidden')
      card.classList.remove('pcard-v2--enable-hover')
    }
  }

  if (card.dataset.hidePrice !== 'true') {
    const baseAmount = variant.price
    const regularPriceValue = variant.price_varies
      ? formatCardPrice(card, baseAmount)
      : formatMoney(baseAmount)
    const regularPrice = `${regularPriceValue}${unit ? ` / ${unit}` : ''}`
    const compareAtPrice = Number(variant.compare_at_price || 0)
    const hasSale = unit === '' && compareAtPrice > Number(variant.price)

    const regularPriceElement = card.querySelector('.js-product-card-v2-price-regular')
    const salePriceElement = card.querySelector('.js-product-card-v2-price-sale-value')
    const comparePriceElement = card.querySelector('.js-product-card-v2-price-compare')
    const saleWrapper = card.querySelector('.js-product-card-v2-price-sale')
    const priceWrapper = card.querySelector('.price')

    if (regularPriceElement) regularPriceElement.textContent = regularPrice
    if (salePriceElement) salePriceElement.textContent = regularPrice
    if (comparePriceElement) {
      comparePriceElement.textContent = hasSale ? formatMoney(compareAtPrice) : ''
    }
    if (saleWrapper) {
      saleWrapper.classList.toggle('hidden', !hasSale)
    }
    if (priceWrapper) {
      priceWrapper.classList.toggle('price--on-sale', hasSale)
    }
  }

  card.querySelectorAll('.js-product-card-v2-swatch').forEach((swatchButton, index) => {
    const isActive = index === swatchIndex
    swatchButton.classList.toggle('pcard-v2__swatch--active', isActive)
  })
}

const loadDeferredState = (card) => {
  const stateUrl = card.dataset.stateUrl
  if (!stateUrl || card.dataset.stateRequested === 'true' || getCardState(card)) return

  card.dataset.stateRequested = 'true'

  fetch(stateUrl)
    .then((response) => response.json())
    .then((state) => {
      setCardState(card, state)
      renderSwatchButtons(card, state)
      updateProductCard(card, 0)
    })
    .catch((error) => {
      console.error('Failed to load deferred product card state', error)
    })
}

const observeDeferredCard = (card) => {
  if (!card.dataset.stateUrl || getCardState(card)) return

  if (!('IntersectionObserver' in window)) {
    loadDeferredState(card)
    return
  }

  if (!deferredStateObserver) {
    deferredStateObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        const target = entry.target
        const load = () => loadDeferredState(target)

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(load, { timeout: 1200 })
        } else {
          window.setTimeout(load, 0)
        }

        deferredStateObserver?.unobserve(target)
      })
    }, {
      rootMargin: '300px 0px',
    })
  }

  deferredStateObserver.observe(card)
}

const initCard = (card) => {
  if (initializedCards.has(card)) return

  initializedCards.add(card)

  const stateTemplate = card.nextElementSibling?.matches?.('[data-product-card-state]')
    ? card.nextElementSibling
    : null

  if (stateTemplate) {
    try {
      const state = JSON.parse(stateTemplate.content.textContent.trim())
      setCardState(card, state)
      if (!card.querySelector('.js-product-card-v2-swatch')) {
        renderSwatchButtons(card, state)
      }
    } catch (error) {
      console.error('Failed to parse product card state', error)
    }
  } else if (card.dataset.deferState === 'true') {
    observeDeferredCard(card)
  }
}

const initWithin = (root = document) => {
  const cards = root.matches?.('.js-product-card-v2')
    ? [root]
    : [...root.querySelectorAll?.('.js-product-card-v2') || []]

  const eventRoot = root.getRootNode?.() || document
  if (eventRoot && !initializedRoots.has(eventRoot)) {
    eventRoot.addEventListener('click', handleCardClick)
    initializedRoots.add(eventRoot)
  }

  cards.forEach(initCard)
}

document.addEventListener('DOMContentLoaded', () => {
  initWithin(document)
})

window.NovaProductCardV2 = {
  initWithin,
}
